import { useState } from 'react';
import {
    CreditCard, Banknote, Sparkles, AlertTriangle, Camera, Check, ChevronDown,
    X as XIcon, Loader2, Send,
} from 'lucide-react';
import { SheetButton, SheetTextarea, SheetAlert } from './SheetUI';
import { SHEET_TOKENS, getTextColors, getInnerCardSurface } from './sheetStyle';
import PenezenkaModal from './PenezenkaModal';
import ZamkyUzaverky from './ZamkyUzaverky';

export const fmtKc = (n) => (n == null || isNaN(n)
    ? '—'
    : Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč');

export const fmtKcZnamenko = (n) => {
    if (n == null || isNaN(n)) return '—';
    const v = Math.round(Number(n));
    if (v === 0) return '0 Kč';
    return (v > 0 ? '+' : '−') + Math.abs(v).toLocaleString('cs-CZ') + ' Kč';
};

/**
 * Uzávěrka pokladny — **pracovní plocha fáze ukončení**, ne samostatný sheet.
 *
 * Sedí pod stálým pruhem cockpitu, takže osa fází zůstává na očích a je pořád
 * vidět, kde ve směně člověk je. Vlastní hlavička („Uzávěrka pokladny /
 * Zelený flek") proto odpadla — byl by to titulek nad titulkem a bral by
 * výšku, kterou potřebují vstupy.
 *
 * **Pozadí žádné.** Plocha modulu je záře aplikace; sheetový povrch roztažený
 * přes obrazovku by ji jen rozmazal a jeho hrana by se četla jako předěl
 * (norma rozvržení, viz `standard-rozvrzeni.md`).
 *
 * Terminál a hotovost stojí **vedle sebe jako dvě dlaždice** — jsou to dvě
 * měření téže chvíle a pod sebou zbytečně natahovaly stránku.
 *
 * Dvě pravidla, na kterých to stojí:
 *
 *  1. **Terminál je povinný a prázdný.** Dřív v něm jako placeholder svítila
 *     systémová kartová tržba a `VyuctovaniSheet` ji při odeslání dosadil.
 *     Kdo formulář proklikl, měl kartovou stranu vždycky v nule: ne proto, že
 *     seděla, ale protože se porovnala sama se sebou. Přitom je to jediný
 *     **nezávislý** údaj v celé uzávěrce.
 *  2. **Hotovost je povinná.** Dřív prošla nula i u směny s hotovostní tržbou.
 *     Součet musí dosáhnout aspoň na ranní základ (hlídá `PenezenkaModal`).
 *
 * „K odevzdání manažerovi" tu **není** — patří na závěrečnou obrazovku, kde se
 * teprve započítá mzda a dýško. Tady by to bylo číslo, které se ještě změní.
 */
export default function UzaverkaStranka({
    preview,
    computed,
    draft,
    setDraft,
    endTime,
    submitting = false,
    submitError = null,
    onSubmit,
    onPickOtherTime,
    /* Zámky uzávěrky — rozpracované objednávky a stáří dat ze serveru. */
    prekrocitData = false,
    onPrekrocitData,
    onZkontrolovatZnovu,
    kontroluji = false,
    isDark,
    isMobile,
}) {
    const barvy = getTextColors(isDark);
    const vnitrek = getInnerCardSurface(isDark);
    const [penezenkaOtevrena, setPenezenkaOtevrena] = useState(false);

    const zaklad = preview?.pocatecni_castka || 0;
    // Co jsi zaplatil hotově z tašky (účtenky) — v peněžence to chybět má.
    const nakupy = preview?.H_nakupy_hotove || 0;
    const ocekavano = (preview?.F_hotovostni_trzba || 0) + zaklad - nakupy;
    // Spodní hranice peněženky — základ bez nákupů z tašky. Když nákupy
    // převýší hotovostní tržbu, je v tašce méně než základ a doplní ho manažer.
    const minimum = Math.max(0, zaklad - nakupy);

    const soucetHotovosti = (draft.manualE !== null && draft.manualE !== undefined)
        ? Number(draft.manualE) || 0
        : Object.entries(draft.denom || {}).reduce((s, [n, k]) => s + Number(n) * Number(k || 0), 0);

    const terminalZadan = !!draft.D && String(draft.D).trim() !== '' && Number.isFinite(parseFloat(draft.D));
    const hotovostZadana = soucetHotovosti >= minimum && soucetHotovosti > 0;
    const vyzadujeDolozeni = !!computed?.vyzaduje_foto;
    const dolozeniOk = !vyzadujeDolozeni || (!!draft.fotoPath && !!draft.komentar?.trim());

    // Zámky ze serveru. Rozpracovaná objednávka s tvým jménem — ať visí
    // kdekoli — znamená, že peníze za ni ještě nejsou v peněžence, takže
    // porovnávat ji s tržbou nemá smysl. Stáří dat jde potvrdit vědomě.
    const zamky = preview?.zamky || [];
    const zamekData = zamky.find(z => z.kod === 'data_ze_serveru');
    const zamkyTvrde = zamky.filter(z => !z.prekrocitelny);
    const zamceno = zamkyTvrde.length > 0 || (!!zamekData && !prekrocitData);

    const lzeOdeslat = terminalZadan && hotovostZadana && dolozeniOk && !zamceno && !submitting;

    const netto = computed ? +(computed.net || 0).toFixed(2) : 0;
    const hotovo = !!computed?.hotovo;

    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{
                flex: 1, overflowY: 'auto', overscrollBehavior: 'contain',
                padding: `4px ${SHEET_TOKENS.spacing.sheetPadX}px 12px`,
                display: 'flex', flexDirection: 'column', gap: 16,
            }}>
                {/* Překážky napřed — ať je vidět, proč je patička zamčená. */}
                <ZamkyUzaverky
                    zamky={zamky}
                    neukoncene={preview?.neukoncene || []}
                    dataServer={preview?.data_ze_serveru}
                    cekaNaPlatbu={preview?.ceka_na_platbu || 0}
                    prekrocit={prekrocitData}
                    onPrekrocit={onPrekrocitData}
                    onZnovu={onZkontrolovatZnovu}
                    nacitam={kontroluji}
                    isDark={isDark}
                />

                <Sekce nadpis="Co říká systém" isDark={isDark}>
                    <Radek popis="Tržba hotovost" hodnota={fmtKc(preview?.F_hotovostni_trzba)} isDark={isDark} />
                    <Radek popis="Tržba karta" hodnota={fmtKc(preview?.G_kartova_trzba)} isDark={isDark} />
                    <Radek popis="Online (vč. dýška)" hodnota={fmtKc(preview?.B_online)} isDark={isDark} />
                    <Radek popis="Ráno jsi dostal" hodnota={fmtKc(zaklad)} isDark={isDark} />
                    {nakupy > 0 && (
                        <Radek popis="Zaplatil jsi z tašky" detail="účtenky dáš manažerovi místo peněz"
                            hodnota={'−' + fmtKc(nakupy)} isDark={isDark} />
                    )}
                </Sekce>

                {/* Dvě měření téže chvíle — vedle sebe, ne pod sebou. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Dlazdice
                            Ikona={CreditCard} label="Terminál" akcent="#3B82F6"
                            vyplneno={terminalZadan} isDark={isDark}
                        >
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={draft.D || ''}
                                    onChange={e => setDraft({ D: e.target.value.replace(/[^\d.,]/g, '') })}
                                    placeholder="—"
                                    aria-label="Částka z uzávěrky terminálu"
                                    style={{
                                        flex: 1, minWidth: 0, background: 'transparent',
                                        border: 'none', outline: 'none', color: barvy.textPrimary,
                                        fontSize: 20, fontWeight: 800, letterSpacing: -0.4,
                                        fontVariantNumeric: 'tabular-nums', padding: 0,
                                    }}
                                />
                                <span style={{ fontSize: 12, fontWeight: 700, color: barvy.textMuted, flexShrink: 0 }}>Kč</span>
                            </div>
                        </Dlazdice>

                        <Dlazdice
                            Ikona={Banknote} label="Hotovost" akcent="#FF8A00"
                            vyplneno={hotovostZadana}
                            onClick={() => setPenezenkaOtevrena(true)}
                            isDark={isDark}
                        >
                            <span style={{
                                display: 'block',
                                fontSize: hotovostZadana ? 20 : 15,
                                fontWeight: 800, letterSpacing: -0.4,
                                fontVariantNumeric: 'tabular-nums',
                                color: hotovostZadana ? barvy.textPrimary : '#FF8A00',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                {hotovostZadana ? fmtKc(soucetHotovosti) : 'Napočítat'}
                            </span>
                        </Dlazdice>
                    </div>

                    <span style={{
                        fontSize: 12.5, lineHeight: 1.4,
                        color: (terminalZadan && hotovostZadana) ? barvy.textMuted : '#FF8A00',
                    }}>
                        {!terminalZadan && !hotovostZadana
                            ? `Opiš číslo z terminálu a napočítej peněženku — základ ráno byl ${fmtKc(zaklad)}.`
                            : !terminalZadan ? 'Zbývá opsat číslo z uzávěrky terminálu.'
                            : !hotovostZadana ? `Zbývá napočítat peněženku — základ ráno byl ${fmtKc(zaklad)}.`
                            : 'Obojí zadané, zkontroluj výsledek.'}
                    </span>
                </div>

                <VysledekVerdikt
                    hotovo={hotovo}
                    net={netto}
                    poznamkaManka="Vyřešíš to s manažerem při vyrovnání."
                    radky={[
                        { popis: 'Karta', hodnota: terminalZadan ? fmtKcZnamenko(computed?.dyskoKarta) : 'čeká na terminál',
                          tlumene: !terminalZadan, barva: terminalZadan ? znamenkoBarva(computed?.dyskoKarta) : null },
                        { popis: 'Hotovost', hodnota: hotovostZadana ? fmtKcZnamenko(computed?.dyskoHotovost) : 'čeká na součet',
                          tlumene: !hotovostZadana, barva: hotovostZadana ? znamenkoBarva(computed?.dyskoHotovost) : null },
                        { popis: 'Online dýško', hodnota: fmtKc(computed?.dyskoOnline) },
                    ]}
                    isDark={isDark}
                />

                {vyzadujeDolozeni && (
                    <Sekce nadpis="Dolož manko" isDark={isDark} barva="#EF4444" Ikona={AlertTriangle}>
                        <SheetAlert kind="error" isDark={isDark}>
                            Manko {fmtKc(Math.abs(netto))} přesahuje limit {fmtKc(computed?.eskLimit)} —
                            vyřeší se s manažerem při závěru směny. Pokud se najde chyba
                            (ztracená objednávka, špatné zaúčtování), bude ti částka doplacena zpětně.
                        </SheetAlert>
                        <FotoPole
                            fotoPath={draft.fotoPath}
                            onNahrano={(path) => setDraft({ fotoPath: path })}
                            onZrusit={() => setDraft({ fotoPath: null })}
                            isDark={isDark}
                        />
                        <SheetTextarea
                            isDark={isDark}
                            label="Komentář (povinný)"
                            error={!draft.komentar?.trim() ? 'Vyplň komentář' : null}
                            placeholder="Např. zákazník nevrátil drobné, ztratil jsem peníze…"
                            value={draft.komentar || ''}
                            onChange={e => setDraft({ komentar: e.target.value })}
                        />
                    </Sekce>
                )}

                {submitError && <SheetAlert kind="error" isDark={isDark}>{submitError}</SheetAlert>}
            </div>

            <div style={{
                flexShrink: 0,
                padding: `0 ${SHEET_TOKENS.spacing.sheetPadX}px`,
                display: 'flex', gap: 8,
            }}>
                <SheetButton
                    isDark={isDark}
                    variant="primary"
                    accent={hotovo && netto < 0 ? '#EF4444' : '#1D9E75'}
                    accent2={hotovo && netto < 0 ? '#F87171' : '#10B981'}
                    icon={<Send size={16} />}
                    busy={submitting}
                    busyLabel="Odesílám…"
                    disabled={!lzeOdeslat}
                    onClick={onSubmit}
                    style={{ flex: 1 }}
                >
                    {endTime ? `Uzavřít v ${endTime}` : 'Uzavřít pokladnu'}
                </SheetButton>
                {onPickOtherTime && (
                    <SheetButton
                        isDark={isDark} variant="secondary"
                        onClick={onPickOtherTime} disabled={!lzeOdeslat}
                        fullWidth={false}
                        style={{ flex: '0 0 auto', padding: '0 14px', fontSize: 13 }}
                    >
                        Jiný čas
                    </SheetButton>
                )}
            </div>

            {penezenkaOtevrena && (
                <PenezenkaModal
                    zaklad={minimum}
                    popisZakladu={nakupy > 0 ? `Základ ráno ${fmtKc(zaklad)} · z tašky −${fmtKc(nakupy)}` : null}
                    ocekavano={ocekavano}
                    denom={draft.denom}
                    manualE={draft.manualE}
                    onZmena={setDraft}
                    onHotovo={() => setPenezenkaOtevrena(false)}
                    onClose={() => setPenezenkaOtevrena(false)}
                    isDark={isDark}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
}

export const znamenkoBarva = (v) => {
    if (v == null || isNaN(v)) return null;
    const n = Math.round(Number(v));
    if (n > 0) return '#10B981';
    if (n < 0) return '#EF4444';
    return null;
};

/**
 * Vstupní dlaždice — terminál a hotovost mají stejný tvar, protože jsou to
 * dvě měření téže chvíle. Nevyplněná má přerušovaný okraj, takže je na první
 * pohled poznat, co ještě chybí.
 */
export function Dlazdice({ Ikona, label, akcent, vyplneno, onClick, isDark, children }) {
    const barvy = getTextColors(isDark);
    const vnitrek = getInnerCardSurface(isDark);
    const Prvek = onClick ? 'button' : 'div';
    return (
        <Prvek
            {...(onClick ? { type: 'button', onClick } : null)}
            style={{
                flex: 1, minWidth: 0,
                display: 'flex', flexDirection: 'column', gap: 5,
                padding: '10px 12px',
                background: vnitrek.background,
                border: `1px ${vyplneno ? 'solid' : 'dashed'} ${vyplneno ? akcent : '#FF8A00'}`,
                borderRadius: SHEET_TOKENS.radius.input,
                boxShadow: vyplneno ? `0 0 0 3px ${akcent}1F` : 'none',
                textAlign: 'left', font: 'inherit',
                cursor: onClick ? 'pointer' : 'default',
                WebkitTapHighlightColor: 'transparent',
            }}>
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
                textTransform: 'uppercase', color: vyplneno ? akcent : barvy.textMuted,
            }}>
                <Ikona size={11} />
                {label}
            </span>
            {children}
        </Prvek>
    );
}

/**
 * Výsledek uzávěrky jako **věta, ne výkaz**.
 *
 * Stály tu čtyři řádky rozdílů (karta / hotovost / online / celkem) a člověk
 * z nich musel sám vyčíst, jestli je to dobře. Přitom potřebuje jedinou
 * odpověď: sedí to, nebo ne — a kolik si nese domů. Rozpis zůstává o jedno
 * klepnutí dál pro toho, kdo chce vidět, z čeho to vyšlo.
 *
 * @param {boolean} hotovo - jsou zadané obě strany (terminál i hotovost)?
 * @param {number} net - znaménkové: kladné = dýško, záporné = manko
 * @param {Array} radky - {popis, hodnota, barva, tlumene} do rozpisu
 * @param {string} [poznamkaManka] - co se stane s mankem; doplní se za větu
 */
export function VysledekVerdikt({ hotovo, net = 0, radky = [], poznamkaManka = null, vetaCekani = 'Doplň terminál a hotovost, pak uvidíš výsledek.', isDark }) {
    const barvy = getTextColors(isDark);
    const [rozpisOtevren, setRozpisOtevren] = useState(false);
    const manko = net < -0.5;
    const dysko = net > 0.5;
    const akcent = !hotovo ? barvy.textMuted : manko ? '#EF4444' : '#10B981';
    const neutral = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
    const neutralOkraj = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    let veta;
    if (!hotovo) veta = vetaCekani;
    else if (manko) veta = 'Chybí ' + fmtKc(-net) + '.' + (poznamkaManka ? ' ' + poznamkaManka : '');
    else if (dysko) veta = 'Sedí to. Vyděláno na dýšcích ' + fmtKc(net) + '.';
    else veta = 'Sedí to, na korunu.';

    return (
        <div style={{
            padding: '12px 14px', borderRadius: SHEET_TOKENS.radius.card,
            background: !hotovo ? neutral : (manko ? 'rgba(239,68,68,0.10)' : 'rgba(16,185,129,0.10)'),
            border: '1px solid ' + (!hotovo ? neutralOkraj : (manko ? 'rgba(239,68,68,0.32)' : 'rgba(16,185,129,0.32)')),
            display: 'flex', flexDirection: 'column', gap: 8,
        }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ color: akcent, flexShrink: 0, marginTop: 1 }}>
                    {!hotovo ? <Sparkles size={16} /> : manko ? <AlertTriangle size={16} /> : <Check size={16} />}
                </span>
                <span style={{ fontSize: 14, lineHeight: 1.45, color: barvy.textPrimary, fontWeight: 600 }}>
                    {veta}
                </span>
            </div>

            {radky.length > 0 && (
                <>
                    <button type="button" onClick={() => setRozpisOtevren(v => !v)}
                        style={{
                            alignSelf: 'flex-start', background: 'transparent', border: 'none',
                            padding: '2px 0', cursor: 'pointer', font: 'inherit',
                            fontSize: 12.5, fontWeight: 600, color: barvy.textMuted,
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}>
                        Rozpis
                        <ChevronDown size={13} style={{
                            transform: rozpisOtevren ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.18s ease-out',
                        }} />
                    </button>
                    {rozpisOtevren && (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {radky.map((r, i) => (
                                <Radek key={i} popis={r.popis} hodnota={r.hodnota}
                                    barva={r.barva} tlumene={r.tlumene} isDark={isDark} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export function Sekce({ nadpis, barva, Ikona, isDark, children }) {
    const barvy = getTextColors(isDark);
    const akcent = barva || barvy.textSecondary;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: SHEET_TOKENS.typography.section.size,
                fontWeight: SHEET_TOKENS.typography.section.weight,
                letterSpacing: SHEET_TOKENS.typography.section.letterSpacing,
                textTransform: SHEET_TOKENS.typography.section.textTransform,
                color: akcent,
            }}>
                {Ikona && <Ikona size={12} />}
                {nadpis}
            </span>
            {children}
        </div>
    );
}

export function Radek({ popis, detail = null, hodnota, barva, tlumene = false, velky = false, isDark }) {
    const barvy = getTextColors(isDark);
    return (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '2px 0' }}>
            <span style={{
                flex: 1, minWidth: 0,
                fontSize: velky ? 14 : 13.5,
                fontWeight: velky ? 700 : 400,
                color: barvy.textSecondary,
                display: 'flex', flexDirection: 'column',
            }}>
                {popis}
                {/* Druhý řádek pod popisem — rozpad položky (např. „záloha 500
                    + dýško 120"), aby souhrnná částka vpravo šla přečíst. */}
                {detail && (
                    <span style={{ fontSize: 11, color: barvy.textMuted, lineHeight: 1.35 }}>{detail}</span>
                )}
            </span>
            <span style={{
                flexShrink: 0,
                fontSize: tlumene ? 12.5 : (velky ? 18 : 13.5),
                fontWeight: tlumene ? 400 : (velky ? 800 : 700),
                fontVariantNumeric: 'tabular-nums',
                color: tlumene ? barvy.textMuted : (barva || barvy.textPrimary),
            }}>{hodnota}</span>
        </div>
    );
}

/** Foto peněženky a lístku z terminálu — povinné u manka nad limit. */
function FotoPole({ fotoPath, onNahrano, onZrusit, isDark }) {
    const barvy = getTextColors(isDark);
    const [nahrava, setNahrava] = useState(false);
    const [chyba, setChyba] = useState(null);

    const vyber = async (e) => {
        const soubor = e.target.files?.[0];
        if (!soubor) return;
        setNahrava(true);
        setChyba(null);
        try {
            const fd = new FormData();
            fd.append('foto', soubor);
            const r = await fetch('/api/me/upload-foto-penezenky', {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content },
                body: fd,
            });
            const data = await r.json().catch(() => null);
            if (!r.ok || !data?.path) throw new Error(data?.error || 'Nahrání selhalo');
            onNahrano(data.path);
        } catch (err) {
            setChyba(err.message || 'Nahrání selhalo');
        } finally {
            setNahrava(false);
        }
    };

    if (fotoPath) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10,
                background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.35)',
            }}>
                <Camera size={16} style={{ color: '#10B981', flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 13, color: barvy.textPrimary }}>Fotka nahraná</span>
                <button type="button" onClick={onZrusit} aria-label="Odebrat fotku"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: barvy.textMuted, padding: 4 }}>
                    <XIcon size={15} />
                </button>
            </div>
        );
    }

    return (
        <label style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            padding: '16px 12px', borderRadius: 10, cursor: nahrava ? 'wait' : 'pointer',
            border: '1px dashed #FF8A00', background: 'rgba(255,138,0,0.06)',
            textAlign: 'center',
        }}>
            <input type="file" accept="image/*" capture="environment" onChange={vyber} disabled={nahrava}
                style={{ display: 'none' }} />
            {nahrava ? <Loader2 size={20} style={{ color: '#FF8A00' }} /> : <Camera size={20} style={{ color: '#FF8A00' }} />}
            <span style={{ fontSize: 13, fontWeight: 700, color: barvy.textPrimary }}>
                {nahrava ? 'Nahrávám…' : 'Vyfoť rozloženou hotovost a lístek z terminálu'}
            </span>
            {chyba && <span style={{ fontSize: 12, color: '#ef4444' }}>{chyba}</span>}
        </label>
    );
}
